import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Gift, Inbox, Mail, Phone, Search, X } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, Empty, Field, Modal, NumberInput, PageHead, Segmented, Select, Spinner, useToast } from "../../components/ui";
import { initials } from "../../lib/format";
import { useT } from "../../i18n/I18nProvider";
import { useFmt } from "./format";
import { useDebounced } from "./Orgs";

const TONE: Record<S["TrialRequestStatus"], "info" | "warn" | "ok" | "danger"> = { new: "info", contacted: "warn", granted: "ok", declined: "danger" };

function Review({ request, onClose }: { request: S["TrialRequest"]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const { date, lux, num } = useFmt();
  const [q, setQ] = useState(request.company);
  const [orgId, setOrgId] = useState<string | null>(request.organization_id ?? null);
  const [credits, setCredits] = useState<number | null>(25);
  const [months, setMonths] = useState<string>("12"); // the trial Lux's expiry (required with a grant)
  const query = useDebounced(q.trim());
  const orgs = useQuery({
    queryKey: ["admin-orgs", query],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs", { params: { query: { q: query || undefined, limit: 8 } } })),
    placeholderData: (prev) => prev,
  });
  const update = useMutation({
    mutationFn: (body: S["TrialRequestUpdate"]) =>
      unwrap(api.PATCH("/v1/admin/trial-requests/{request_id}", { params: { path: { request_id: request.id } }, body })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["trials"] });
      toast(t("admin.trials.updated", { status: r.status }));
      onClose();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <Modal title={t("admin.trials.reviewTitle", { company: request.company })} onClose={onClose} width={640}>
      <div className="stack">
        <div className="grid-2">
          <div className="stack" style={{ gap: 6 }}>
            <b dir="auto">{request.name}</b>
            <span className="row muted small"><Mail size={14} /> <a href={`mailto:${request.email}`} dir="auto">{request.email}</a></span>
            {request.phone && <span className="row muted small"><Phone size={14} /> <span dir="auto">{request.phone}</span></span>}
          </div>
          <div className="stack small muted" style={{ gap: 6 }}>
            <span><FormattedMessage id="admin.trials.country" values={{ country: request.country, b: (c) => <b dir="auto">{c}</b> }} /></span>
            <span>
              <FormattedMessage
                id="admin.trials.volume"
                values={{ volume: request.volume ?? 0, known: request.volume != null ? "yes" : "no", b: (c) => <b>{c}</b> }}
              />
            </span>
            <span>{t("admin.trials.received", { when: date(request.created_at, true) })}</span>
          </div>
        </div>
        {request.message && <div className="alert" dir="auto">{request.message}</div>}
        <div className="divider" />
        <h3>{t("admin.trials.stepLink")}</h3>
        <p className="small muted">{t("admin.trials.stepLinkHint")}</p>
        <div className="input-icon">
          <Search />
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.trials.orgSearchPlaceholder")} aria-label={t("admin.trials.orgSearchPlaceholder")} />
        </div>
        <div className="stack" style={{ gap: 6, maxBlockSize: 200, overflowY: "auto" }}>
          {orgs.data?.items.map(({ organization: o, balance }) => (
            <button
              key={o.id}
              className="btn"
              style={{ justifyContent: "flex-start", blockSize: "auto", paddingBlock: 8, paddingInline: 10, borderColor: orgId === o.id ? "var(--primary)" : undefined, boxShadow: orgId === o.id ? "0 0 0 3px var(--ring)" : undefined }}
              onClick={() => setOrgId(o.id)}
            >
              <span className="org-avatar" style={{ inlineSize: 26, blockSize: 26, fontSize: 11 }}>{initials(o.name)}</span>
              <span style={{ flex: 1, textAlign: "start" }} dir="auto">{o.name}</span>
              <span className="small faint">{t("admin.trials.balance", { lux: lux(balance) })}</span>
            </button>
          ))}
          {orgs.data && !orgs.data.items.length && <span className="small faint">{t("admin.trials.noOrgMatch")}</span>}
        </div>
        <h3>{t("admin.trials.stepGrant")}</h3>
        <div className="form-grid">
          <Field label={t("admin.trials.luxWhole")}>
            <NumberInput value={credits} min={1} max={100000} step={5} onChange={setCredits} />
          </Field>
          <Field label={t("admin.trials.expires")} hint={t("admin.trials.expiresHint")}>
            <Select
              value={months}
              onChange={setMonths}
              ariaLabel={t("admin.trials.expires")}
              options={["1", "3", "6", "12"].map((m) => ({ value: m, label: t("admin.trials.inMonths", { months: Number(m) }) }))}
            />
          </Field>
        </div>
        <div className="card-foot" style={{ marginBlock: "4px -20px", marginInline: -20, justifyContent: "space-between" }}>
          <div className="row">
            <Button variant="danger" icon={<X />} loading={update.isPending} onClick={() => update.mutate({ status: "declined" })}>
              {t("admin.trials.decline")}
            </Button>
            <Button loading={update.isPending} onClick={() => update.mutate({ status: "contacted" })} icon={<Mail />}>
              {t("admin.trials.markContacted")}
            </Button>
          </div>
          <Button
            variant="primary"
            icon={<Gift />}
            disabled={!orgId || !credits}
            loading={update.isPending}
            onClick={() => update.mutate({ status: "granted", organization_id: orgId, grant_lux: credits ? credits * 100 : undefined, ...(credits ? { grant_expires: { months: Number(months) } } : {}) })}
          >
            {t("admin.trials.grantAndClose", { lux: num(credits ?? 0) })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function AdminTrials() {
  const t = useT();
  const { num, relative } = useFmt();
  const [status, setStatus] = useState<"all" | S["TrialRequestStatus"]>("new");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<S["TrialRequest"] | null>(null);
  const query = useDebounced(q.trim());
  const trials = useQuery({
    queryKey: ["trials", status, query],
    queryFn: () =>
      unwrap(api.GET("/v1/admin/trial-requests", { params: { query: { status: status === "all" ? undefined : status, q: query || undefined, limit: 100 } } })),
    placeholderData: (prev) => prev,
  });
  return (
    <>
      <PageHead title={t("admin.trials.title")} description={t("admin.trials.description")} />
      <Card pad={false}>
        <div className="toolbar">
          <div className="input-icon">
            <Search />
            <input className="input" placeholder={t("admin.trials.searchPlaceholder")} aria-label={t("admin.trials.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: "new", label: t("admin.trials.filterNew") },
              { value: "contacted", label: t("admin.trials.filterContacted") },
              { value: "granted", label: t("admin.trials.filterGranted") },
              { value: "declined", label: t("admin.trials.filterDeclined") },
              { value: "all", label: t("admin.trials.filterAll") },
            ]}
          />
        </div>
        {trials.isLoading ? (
          <Spinner />
        ) : trials.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(trials.error)}</Alert>
          </div>
        ) : !trials.data?.items.length ? (
          <Empty icon={<Inbox />} title={status === "new" ? t("admin.trials.inboxZero") : t("admin.trials.nothingHere")}>
            {status === "new" ? t("admin.trials.noNew") : t("admin.trials.noMatch")}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("admin.trials.company")}</th>
                  <th className="hide-sm">{t("admin.trials.contact")}</th>
                  <th className="num hide-sm">{t("admin.trials.volumeColumn")}</th>
                  <th>{t("admin.trials.status")}</th>
                  <th className="num">{t("admin.trials.receivedColumn")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {trials.data.items.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setOpen(r)}>
                    <td>
                      <div className="row" style={{ flexWrap: "nowrap" }}>
                        <div className="org-avatar">{initials(r.company)}</div>
                        <div style={{ minInlineSize: 0 }}>
                          <div className="cell-main ellipsis" dir="auto">{r.company}</div>
                          <div className="cell-sub ellipsis" dir="auto">
                            {r.message
                              ? t("admin.trials.countryAndMessage", { country: r.country, message: r.message.length > 60 ? `${r.message.slice(0, 60)}…` : r.message })
                              : r.country}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hide-sm">
                      <div className="cell-main" dir="auto">{r.name}</div>
                      <div className="cell-sub" dir="auto">{r.email}</div>
                    </td>
                    <td className="num hide-sm">{r.volume != null ? t("admin.trials.perMonth", { volume: num(r.volume) }) : "—"}</td>
                    <td>
                      <Badge tone={TONE[r.status]}>{t("admin.trials.statusBadge", { status: r.status })}</Badge>
                      {r.organization_id && (
                        <Link to={`/admin/orgs/${r.organization_id}`} onClick={(e) => e.stopPropagation()} style={{ marginInlineStart: 6 }} aria-label={t("admin.trials.openOrg")} title={t("admin.trials.openOrg")}>
                          <Building2 size={14} style={{ verticalAlign: -2 }} />
                        </Link>
                      )}
                    </td>
                    <td className="num">{relative(r.created_at)}</td>
                    <td className="right">
                      <Button size="sm">{t("admin.trials.review")}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {open && <Review request={open} onClose={() => setOpen(null)} />}
    </>
  );
}
